import dayjs from 'dayjs';
import type { SxProps, Theme } from '@mui/material';

import type {
  OnboardingStepStatus,
  ShopifyMerchantOnboarding,
  ShopifyOnboardingStep,
  ShopifyOnboardingStepKey,
} from '../../store/modules/merchant/types';
import { shopifyOnboardingStepKeys } from '../../store/modules/merchant/types';
import {
  colorDisabled,
  colorError,
  colorSuccess,
  colorWarning,
  type StatusColor,
} from '../../util/constants';

export type PortalEnvironment = 'dev' | 'staging' | 'prod';

/** Same horizontal padding as Api Keys, Click to Pay, and Paylinks table actions. Height stays the default 50px. */
export const buttonPaddingSx: SxProps<Theme> = {
  px: 4,
  whiteSpace: 'nowrap',
  alignSelf: 'flex-start',
  flexShrink: 0,
  '& .MuiTypography-root': { whiteSpace: 'nowrap' },
};

/** Same reading-width cap as Create Merchant. */
export const contentMaxWidthSx: SxProps<Theme> = {
  maxWidth: { xs: '100%', md: '500px', lg: '800px' },
  width: '100%',
};

export const SHOPIFY_STEP_LABELS: Record<ShopifyOnboardingStepKey, string> = {
  app_init: 'Create app',
  distribution: 'Distribution and allowlist',
  app_deploy: 'Deploy app',
  credentials_saved: 'Partner keys saved',
  oauth_complete: 'Complete OAuth',
  embed_enabled: 'Enable theme embed',
};

export const ONBOARDING_STATUS_LABELS: Record<OnboardingStepStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  done: 'Done',
  blocked: 'Blocked',
  failed: 'Failed',
};

export const ONBOARDING_STATUS_COLORS: Record<OnboardingStepStatus, StatusColor> = {
  pending: colorDisabled,
  in_progress: colorWarning,
  done: colorSuccess,
  blocked: colorWarning,
  failed: colorError,
};

export const getStepLabel = (key: ShopifyOnboardingStepKey): string =>
  SHOPIFY_STEP_LABELS[key] ?? key;

export const getStatusLabel = (status: OnboardingStepStatus): string =>
  ONBOARDING_STATUS_LABELS[status];

const PORTAL_HOSTS: Record<string, PortalEnvironment> = {
  'admin.skipify.com': 'prod',
  'admin.staging.skipify.com': 'staging',
  'admin.dev.skipify.com': 'dev',
};

/**
 * Which Skipify environment this portal belongs to, read from the host it is served on.
 */
export const getPortalEnvironment = (hostname = window.location.hostname): PortalEnvironment =>
  PORTAL_HOSTS[hostname.toLowerCase()] ?? 'dev';

/** Shopify app names are capped at 30 characters. Never drop `ella-` or `-{env}`. */
const SHOPIFY_APP_NAME_MAX_LENGTH = 30;

export const buildAppName = (shopHandle: string, environment: PortalEnvironment): string => {
  const prefix = 'ella-';
  const suffix = `-${environment}`;
  const handleBudget = SHOPIFY_APP_NAME_MAX_LENGTH - prefix.length - suffix.length;
  const handle = shopHandle.slice(0, Math.max(0, handleBudget)).replace(/-+$/u, '');
  return `${prefix}${handle}${suffix}`;
};

export const getShopHandle = (shopDomain: string): string =>
  shopDomain.replace(/\.myshopify\.com$/i, '');

export const getStep = (
  onboarding: ShopifyMerchantOnboarding | undefined,
  key: ShopifyOnboardingStep['key'],
) => onboarding?.steps.find((step) => step.key === key);

export const getCurrentStep = (
  onboarding: ShopifyMerchantOnboarding,
): ShopifyOnboardingStep['key'] => {
  const active = onboarding.steps.find(
    (step) => step.status === 'in_progress' || step.status === 'failed',
  );
  if (active) return active.key;

  const next = onboarding.steps.find(
    (step) => step.status === 'pending' || step.status === 'blocked',
  );
  return next?.key ?? 'embed_enabled';
};

export const isOnboardingComplete = (onboarding: ShopifyMerchantOnboarding): boolean =>
  onboarding.steps.length > 0 && onboarding.steps.every((step) => step.status === 'done');

/**
 * What this onboarding is waiting on. `getCurrentStep` falls back to the last step once nothing is
 * outstanding, which would read as unfinished work, so a finished onboarding says so instead.
 */
export const getCurrentStepLabel = (onboarding: ShopifyMerchantOnboarding): string =>
  isOnboardingComplete(onboarding) ? 'Complete' : getStepLabel(getCurrentStep(onboarding));

export const AUTOMATION_TOKEN_LIFETIME_MONTHS = 6;

/** Warn this far ahead so a token gets rotated before it blocks an onboarding. */
const TOKEN_EXPIRY_WARNING_DAYS = 14;

export const buildTokenExpiryDate = (): string =>
  dayjs().add(AUTOMATION_TOKEN_LIFETIME_MONTHS, 'month').format('YYYY-MM-DD');

export type TokenExpiryState = {
  label: string;
  color: StatusColor;
  isExpired: boolean;
};

export const describeTokenExpiry = (expiresAt?: string): TokenExpiryState | undefined => {
  if (!expiresAt) return undefined;

  const expiry = dayjs(expiresAt);
  if (!expiry.isValid()) return undefined;

  const daysLeft = expiry.startOf('day').diff(dayjs().startOf('day'), 'day');
  const formatted = expiry.format('MMM D, YYYY');

  if (daysLeft < 0) {
    return { label: `Expired ${formatted}`, color: colorError, isExpired: true };
  }
  if (daysLeft <= TOKEN_EXPIRY_WARNING_DAYS) {
    return {
      label: `Expires ${formatted} (${daysLeft === 0 ? 'today' : `${daysLeft}d`})`,
      color: colorWarning,
      isExpired: false,
    };
  }
  return { label: `Expires ${formatted}`, color: colorSuccess, isExpired: false };
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object') {
    const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string' && message) return message;
    }
    if ('error' in error && typeof (error as { error?: unknown }).error === 'string') {
      return (error as { error: string }).error;
    }
  }
  return fallback;
};

export const getInstallUrl = (onboarding: ShopifyMerchantOnboarding): string | undefined => {
  const value = getStep(onboarding, 'distribution')?.meta?.installUrl;
  return typeof value === 'string' && value ? value : undefined;
};

/**
 * When this merchant's App Automation Token expires, as recorded on the Create app step.
 */
export const getAutomationTokenExpiry = (
  onboarding: ShopifyMerchantOnboarding,
): string | undefined => {
  const value = getStep(onboarding, 'app_init')?.meta?.appAutomationTokenExpiresAt;
  return typeof value === 'string' && value ? value : undefined;
};

/**
 * Who owns each step. Steps 1 to 4 happen in Skipify's Dev Dashboard; the last two happen in the
 * merchant's own Shopify admin, which is the handoff the detail page draws a divider at.
 */
const STEP_OWNER: Record<ShopifyOnboardingStepKey, 'skipify' | 'merchant'> = {
  app_init: 'skipify',
  credentials_saved: 'skipify',
  distribution: 'skipify',
  app_deploy: 'skipify',
  oauth_complete: 'merchant',
  embed_enabled: 'merchant',
};

export const isMerchantOwnedStep = (key: ShopifyOnboardingStepKey): boolean =>
  STEP_OWNER[key] === 'merchant';

export const MERCHANT_OWNED_STEP_KEYS: readonly ShopifyOnboardingStepKey[] =
  shopifyOnboardingStepKeys.filter(isMerchantOwnedStep);

export const getMerchantOwnedSteps = (
  onboarding: ShopifyMerchantOnboarding,
): ShopifyOnboardingStep[] => onboarding.steps.filter((step) => isMerchantOwnedStep(step.key));

export const areSkipifyStepsComplete = (onboarding: ShopifyMerchantOnboarding): boolean =>
  onboarding.steps
    .filter((step) => !isMerchantOwnedStep(step.key))
    .every((step) => step.status === 'done');

/**
 * Status for the merchant-owned portion of the checklist — what Agent Hub Provisioning shows.
 */
export const getMerchantProvisioningStatus = (
  onboarding: ShopifyMerchantOnboarding,
): ProvisioningStatus => {
  const merchantSteps = getMerchantOwnedSteps(onboarding);

  if (merchantSteps.length > 0 && merchantSteps.every((step) => step.status === 'done')) {
    return { label: 'Complete', color: colorSuccess, needsAction: false };
  }

  if (!areSkipifyStepsComplete(onboarding)) {
    return {
      label: 'Preparing',
      color: colorWarning,
      needsAction: false,
      reason: 'Skipify is completing store setup. Your steps will be ready soon.',
    };
  }

  const active = merchantSteps.find(
    (step) =>
      step.status === 'in_progress' || step.status === 'failed' || step.status === 'blocked',
  );
  if (active?.status === 'failed') {
    return {
      label: 'Failed',
      color: colorError,
      needsAction: true,
      reason: `${getStepLabel(active.key)} failed.`,
    };
  }
  if (active?.status === 'blocked') {
    return {
      label: 'Blocked',
      color: colorError,
      needsAction: true,
      reason: `${getStepLabel(active.key)} is blocked.`,
    };
  }
  if (active?.status === 'in_progress') {
    return {
      label: 'In progress',
      color: colorWarning,
      needsAction: false,
      reason: `${getStepLabel(active.key)} is running.`,
    };
  }

  return {
    label: 'Action needed',
    color: colorWarning,
    needsAction: true,
  };
};

export type ProvisioningStatus = {
  label: string;
  color: StatusColor;
  /** True when an admin has to act. What the queue is really scanned for. */
  needsAction: boolean;
  /** Why, when the label alone does not say it. Rendered as a tooltip. */
  reason?: string;
};

/**
 * Where this onboarding stands
 */
export const getProvisioningStatus = (
  onboarding: ShopifyMerchantOnboarding,
  isTokenExpired = describeTokenExpiry(getAutomationTokenExpiry(onboarding))?.isExpired ?? false,
): ProvisioningStatus => {
  if (isOnboardingComplete(onboarding)) {
    return { label: 'Complete', color: colorSuccess, needsAction: false };
  }

  const key = getCurrentStep(onboarding);
  const status = getStep(onboarding, key)?.status;
  const stepLabel = getStepLabel(key);

  // A stalled step is ours whoever owns it: the merchant cannot clear a failed deploy.
  if (status === 'failed') {
    return {
      label: 'Failed',
      color: colorError,
      needsAction: true,
      reason: `${stepLabel} failed.`,
    };
  }
  if (status === 'blocked') {
    return {
      label: 'Blocked',
      color: colorError,
      needsAction: true,
      reason: `${stepLabel} is blocked.`,
    };
  }
  // A running deploy is nobody's move. It reads as active rather than as work waiting on an admin.
  if (status === 'in_progress') {
    return {
      label: key === 'app_deploy' ? 'Deploying' : 'In progress',
      color: colorWarning,
      needsAction: false,
      reason: `${stepLabel} is running.`,
    };
  }

  if (STEP_OWNER[key] === 'merchant') {
    return {
      label: 'With merchant',
      color: colorDisabled,
      needsAction: false,
      reason: `${stepLabel} happens in the merchant's Shopify admin.`,
    };
  }

  return {
    label: 'Action needed',
    color: colorWarning,
    needsAction: true,
    // An expired token blocks the deploy, so it is the real next action even when the current step
    // is something earlier.
    reason: isTokenExpired
      ? 'The App Automation Token has expired. Rotate it before deploying.'
      : `${stepLabel} is the next action.`,
  };
};
